import {useState} from 'react'
import {useQuery} from 'react-query'
import {fetchInspectionReport} from './api'
import {PageTitle} from '../../../_metronic/layout/core'
import CRUDTable from '../../../components/table/CRUDTable'
import toast, {Toaster} from 'react-hot-toast'
import viewProps from '../viewProps'
import {Error500} from '../../modules/errors/components/Error500'
import TopBarProgress from 'react-topbar-progress-indicator'
import {Processing} from '../../../components/Loaders'
import {Helmet} from 'react-helmet'
import Select from 'react-select'
import {fetchTripNumberList} from '../tripNumber/api'
import {fetchShiftList} from '../shift/api'
import {InspectionReportBase, InspectionReportPayload} from './type'
import {differenceInDays, parseISO} from 'date-fns'
import ExportToExcelButton from './ExportToExcelButton'
import {APIResponse} from '../../../types/type'
import useSchool from '../../hooks/useSchools'


const InspectionReport = () => {
  const [filters, setFilters] = useState<InspectionReportPayload>({
    from_date: '',
    to_date: '',
    trip_number: 'all',
    shift_id: 'all',
    trip_type: 'all',
  })

  const {data: tripNumbers, isLoading: isLoadingTripNums} = useQuery(
    'trip-numbers',
    fetchTripNumberList,
    {
      refetchOnWindowFocus: false,
    }
  )
  const {data: shift, isLoading: isLoadingShifts} = useQuery('shift', fetchShiftList, {
    refetchOnWindowFocus: false,
  })

  const { schoolName } = useSchool()

  const {
    data: reportData,
    isLoading: isLoadingReport,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    'inspection-report',
    () =>
      fetchInspectionReport({
        from_date: filters.from_date,
        to_date: filters.to_date,
        trip_number: filters.trip_number ?? '',
        trip_type: filters.trip_type ?? '',
        shift_id: filters.shift_id ?? '',
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      initialData: {data: []} as Partial<APIResponse<InspectionReportBase[]>>,
      select: (data: any) => {
        let updatedReport = data

        const newKeys = [
          {inspectedBySecurity: ['security_name', 'security_date']},
          {inspectedByTeamLeader: ['team_leader_name', 'team_leader_date']},
          {inspectedByDriver: ['driver_name', 'driver_date']},
          {inspectedByConductor: ['conductor_name', 'conductor_date']},
        ]

        // add new keys for inspection names and dates
        updatedReport.data.forEach((data: any) => {
          newKeys.forEach((key) => {
            Object.entries(key).forEach(([newKey, [nameField, dateField]]) => {
              const date = data[dateField]
              const name = data[nameField] || ''
              if (!date) {
                data[newKey] = name
              } else {
                data[newKey] = `Inspected by ${name} at ${new Date(date).toLocaleTimeString(
                  'en-US',
                  {
                    hour: '2-digit',
                    minute: '2-digit',
                  }
                )}`
              }
            })
          })
        })
        return updatedReport
      },
    }
  )

  const handleApplyFilters = () => {
    const {from_date, to_date, trip_number, shift_id, trip_type} = filters

    if (!from_date || !to_date) {
      toast.error('Please select both From and To dates.')
      return
    }

    if (!trip_number && !shift_id && (!trip_type || trip_type === 'all')) {
      toast.error('Please select at least one filter: Trip Number, Shift, or Trip Type.')
      return
    }
    const fromDate = parseISO(from_date)
    const toDate = parseISO(to_date)

    const daysDiff = differenceInDays(toDate, fromDate)

    if (daysDiff < 0) {
      toast.error('From Date must be before or equal to To Date.')
      return
    }

    toast.loading('Applying filters...')
    refetch()
      .then(() => {
        toast.dismiss()
        toast.success('Filters applied successfully.')
      })
      .catch(() => {
        toast.dismiss()
        toast.error('Failed to fetch inspection report.')
      })
  }

  if (isLoadingShifts || isLoadingTripNums) {
    return <Processing />
  }

  const tripNumberOptions = [{trip_number: 'all'}, ...tripNumbers?.data!].map((item: any) => ({
    label: item.trip_number.toUpperCase(),
    value: item.trip_number,
  }))
  const shiftOptions = [{shift_name: 'all', shift_id: 'all'}, ...shift?.data!].map((item: any) => ({
    label: item.shift_name.toUpperCase(),
    value: item.shift_id,
  }))

  const selectedShiftName =
    shift?.data.find((opt: any) => opt.shift_id === filters.shift_id)?.shift_name || 'All Shifts'
  const selectedTripType =
    filters.trip_type === 'all'
      ? 'All Trip Types'
      : filters.trip_type.charAt(0).toUpperCase() + filters.trip_type.slice(1)

  return (
    <>
      <Helmet>
        <title>TrackNAB | Inspection Report</title>
      </Helmet>
      <PageTitle>Inspection Report</PageTitle>

      {/* Filters Section */}
      <div className="mb-4 p-4 border rounded shadow-sm bg-light">
        <div className="row g-3">
          <div className="col-md-2">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.from_date}
              onChange={(e) => setFilters({...filters, from_date: e.target.value})}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.to_date}
              onChange={(e) => setFilters({...filters, to_date: e.target.value})}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Trip Type</label>
            <select
              className="form-select"
              value={filters.trip_type}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  trip_type: e.target.value as 'all' | 'pickup' | 'drop',
                })
              }
            >
              <option value="all">All</option>
              <option value="pickup">Pickup</option>
              <option value="drop">Drop</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Trip Number</label>
            <Select
              options={tripNumberOptions}
              onChange={(opt: any) => setFilters({...filters, trip_number: opt?.value || null})}
              value={tripNumberOptions.find((opt) => opt.value === filters.trip_number) || null}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Shift</label>
            <Select
              options={shiftOptions}
              onChange={(opt: any) => setFilters({...filters, shift_id: opt?.value || null})}
              value={shiftOptions.find((opt) => opt.value === filters.shift_id) || null}
            />
          </div>
          <div className="col-md-12 d-flex justify-content-end">
            <button className="btn btn-primary mt-2" onClick={handleApplyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {isLoadingReport && <Processing />}
      {isError && <Error500 />}
      {reportData.data.length! > 0 ? (
        <CRUDTable
          data={reportData.data}
          {...viewProps.InspectionReport}
          fieldToLabelMap={viewProps.InspectionReport.primaryFieldsToLabelMap}
          headerExtras={
            <>
              {/* <div className="fs-3">{`Mes School Security Inspection Report - ${selectedShiftName} - ${selectedTripType} - Year`}</div> */}
              <div className="ms-auto">
                <ExportToExcelButton
                  data={reportData.data}
                  fileName="Inspection Report"
                  heading={`${schoolName} Security Inspection Report - ${selectedShiftName} - ${selectedTripType} - ${new Date().getFullYear()}`}
                  titleMap={viewProps.InspectionReport.primaryFieldsToLabelMap}
                />
              </div>
            </>
          }
          // isSearchable={false}
        />
      ) : (
        <div className="fs-2 p-6 text-center">Apply filters / No data for applied filters</div>
      )}

      {isFetching && <TopBarProgress />}
      <Toaster />
    </>
  )
}

export default InspectionReport
